import React, { useState } from 'react';
import { Mail, MapPin, Send, Clock, CheckCircle, ExternalLink } from 'lucide-react';

const CONTACT_EMAIL = 'sithika@alteracode.org';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', courseTrack: '', message: '' });
  const [sent, setSent] = useState(false);

  const trackLabels = {
    'software-engineering': 'EduCenter — Software Engineering',
    'cybersecurity':        'EduCenter — Cybersecurity',
    'ai-ml':                'EduCenter — AI & Machine Learning',
    'project':              'Project / Services',
    'partnership':          'Partnership',
    'other':                'Other',
  };

  const buildSubject = () =>
    `AlteraCode Inquiry${form.courseTrack ? ` — ${trackLabels[form.courseTrack] || form.courseTrack}` : ''}`;

  const buildBody = () =>
    `Name: ${form.name}\n` +
    `Email: ${form.email}\n` +
    (form.phone       ? `Phone: ${form.phone}\n`                              : '') +
    (form.courseTrack ? `Inquiry Type: ${trackLabels[form.courseTrack]}\n`    : '') +
    `\nMessage:\n${form.message}`;

  const handleSubmit = (e) => {
    e.preventDefault();

    const subject = encodeURIComponent(buildSubject());
    const body    = encodeURIComponent(buildBody());

    // Open Outlook Web compose directly — works in any browser, no mail client needed
    const outlookUrl =
      `https://outlook.office.com/mail/deeplink/compose` +
      `?to=${encodeURIComponent(CONTACT_EMAIL)}` +
      `&subject=${subject}` +
      `&body=${body}`;

    window.open(outlookUrl, '_blank', 'noopener,noreferrer');
    setSent(true);
  };

  const openGmail = () => {
    const subject = encodeURIComponent(buildSubject());
    const body    = encodeURIComponent(buildBody());
    const gmailUrl =
      `https://mail.google.com/mail/?view=cm&fs=1` +
      `&to=${encodeURIComponent(CONTACT_EMAIL)}` +
      `&su=${subject}` +
      `&body=${body}`;
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');
  };

  const contactInfo = [
    { Icon: Mail,   label: 'Email',    value: CONTACT_EMAIL,         color: 'var(--ac-cyan)'   },
    { Icon: MapPin, label: 'Location', value: 'Wattala, Sri Lanka',  color: 'var(--ac-green)'  },
    { Icon: Clock,  label: 'Hours',    value: 'Mon–Fri, 9am–6pm IST', color: 'var(--ac-pink)' },
  ];

  return (
    <div style={{ paddingTop: 68 }}>

      {/* Hero */}
      <section style={{
        padding: '4.5rem 0 3rem', textAlign: 'center',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(0,200,255,0.06) 0%, transparent 60%), var(--ac-dark)',
      }}>
        <div className="container">
          <div className="badge badge-cyan" style={{ marginBottom: '1rem' }}>Get In Touch</div>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 52px)', fontWeight: 800, color: 'var(--ac-white)', marginBottom: '1rem' }}>
            Let's <span className="grad-text">Build Together</span>
          </h1>
          <p style={{ fontSize: 16, color: 'var(--ac-muted)', maxWidth: 520, margin: '0 auto' }}>
            Whether you need a tech partner or want to enrol in our EduCenter — we'd love to hear from you.
          </p>
        </div>
      </section>

      <div className="glow-divider" />

      <section className="section">
        <div className="container">
          <div className="layout-2col">

            {/* Info */}
            <div>
              <h2 style={{ fontSize: 26, fontWeight: 700, color: 'var(--ac-white)', marginBottom: '0.8rem' }}>Contact Information</h2>
              <p style={{ fontSize: 14, color: 'var(--ac-muted)', lineHeight: 1.7, marginBottom: '2rem' }}>
                Reach out for project inquiries, partnership discussions, or EduCenter enrolment queries.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginBottom: '2.5rem' }}>
                {contactInfo.map(({ Icon, label, value, color }) => (
                  <div key={label} style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 'var(--radius-md)',
                      background: color + '12', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', border: `1px solid ${color}25`, flexShrink: 0,
                    }}>
                      <Icon size={18} color={color} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--ac-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ac-white)' }}>{value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick open buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  onClick={() => {
                    const url = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(CONTACT_EMAIL)}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'rgba(0,120,212,0.08)', border: '1px solid rgba(0,120,212,0.25)',
                    borderRadius: 'var(--radius-md)', padding: '0.8rem 1.1rem',
                    color: '#2196f3', fontSize: 13, fontWeight: 500,
                    cursor: 'pointer', transition: 'var(--transition)', width: '100%',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,120,212,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,120,212,0.08)'; }}
                >
                  <ExternalLink size={14} /> Open Outlook Directly
                </button>
                <button
                  onClick={() => {
                    const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(CONTACT_EMAIL)}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: 'rgba(234,67,53,0.08)', border: '1px solid rgba(234,67,53,0.2)',
                    borderRadius: 'var(--radius-md)', padding: '0.8rem 1.1rem',
                    color: '#ea4335', fontSize: 13, fontWeight: 500,
                    cursor: 'pointer', transition: 'var(--transition)', width: '100%',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(234,67,53,0.15)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(234,67,53,0.08)'; }}
                >
                  <ExternalLink size={14} /> Open Gmail Directly
                </button>
              </div>
            </div>

            {/* Form */}
            <div style={{
              background: 'var(--ac-card)', border: '1px solid var(--ac-border)',
              borderRadius: 'var(--radius-xl)', padding: '2.5rem',
            }}>
              {sent ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <CheckCircle size={56} color="var(--ac-green)" style={{ marginBottom: '1rem' }} />
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ac-white)', marginBottom: '0.75rem' }}>
                    Outlook Opened!
                  </h3>
                  <p style={{ color: 'var(--ac-muted)', fontSize: 14, lineHeight: 1.7, marginBottom: '1.5rem' }}>
                    Your message is ready in Outlook's compose window.<br />
                    Just hit <strong style={{ color: 'var(--ac-white)' }}>Send</strong> there to deliver it to us.
                  </p>
                  <button
                    className="btn btn-outline"
                    onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', courseTrack: '', message: '' }); }}
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <>
                  <h3 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ac-white)', marginBottom: '0.5rem' }}>
                    Send us a Message
                  </h3>
                  <p style={{ fontSize: 13, color: 'var(--ac-muted)', marginBottom: '1.8rem', lineHeight: 1.6 }}>
                    Fill the form and click Send — Outlook will open in a new tab with everything pre-filled.
                  </p>

                  <form onSubmit={handleSubmit}>
                    <div className="grid-2" style={{ marginBottom: 0 }}>
                      <div className="form-group">
                        <label className="form-label">Full Name *</label>
                        <input
                          className="form-input" placeholder="Your full name"
                          value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Your Email Address *</label>
                        <input
                          className="form-input" type="email" placeholder="you@email.com"
                          value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required
                        />
                      </div>
                    </div>
                    <div className="grid-2" style={{ marginBottom: 0 }}>
                      <div className="form-group">
                        <label className="form-label">Phone Number</label>
                        <input
                          className="form-input" placeholder="+94 71 xxx xxxx"
                          value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Inquiry Type</label>
                        <select className="form-select" value={form.courseTrack} onChange={e => setForm({ ...form, courseTrack: e.target.value })}>
                          <option value="">Select topic</option>
                          <option value="software-engineering">EduCenter — Software Engineering</option>
                          <option value="cybersecurity">EduCenter — Cybersecurity</option>
                          <option value="ai-ml">EduCenter — AI & ML</option>
                          <option value="project">Project / Services</option>
                          <option value="partnership">Partnership</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Message *</label>
                      <textarea
                        className="form-textarea"
                        placeholder="Tell us about your project, inquiry or question..."
                        value={form.message} onChange={e => setForm({ ...form, message: e.target.value })}
                        required rows={5}
                      />
                    </div>

                    {/* Info note */}
                    <div style={{
                      display: 'flex', alignItems: 'flex-start', gap: 9,
                      background: 'rgba(0,120,212,0.06)', border: '1px solid rgba(0,120,212,0.2)',
                      borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: '1.2rem',
                    }}>
                      <Mail size={14} color="#2196f3" style={{ marginTop: 2, flexShrink: 0 }} />
                      <p style={{ fontSize: 12, color: 'var(--ac-muted)', lineHeight: 1.6, margin: 0 }}>
                        Clicking <strong style={{ color: 'var(--ac-white)' }}>Send Message</strong> will open <strong style={{ color: '#2196f3' }}>Outlook</strong> in a new tab with your message pre-filled to <strong style={{ color: 'var(--ac-white)' }}>{CONTACT_EMAIL}</strong>. Just hit Send there.
                      </p>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary"
                      style={{ width: '100%', justifyContent: 'center' }}
                    >
                      <Send size={16} /> Send Message
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
